import { Dayjs } from 'dayjs';
import { IAsset } from './asset';
import { ISKU } from './skus';
import { CurrencyCode } from './base';

export type DCItemAction = 'edit_scan_qc' | 'reject' | 'reset_scan' | 'remove';

export type DCAction =
  | 'send_for_grn'
  | 'grn_done'
  | 'close'
  | 'discard'
  | 'add_items'
  | 'receive'
  | 'import'
  | 'approve';

export type LotAction = 'close' | 'scan_items' | 'export_barcode' | 'export_grn_summary';

export type DCView = 'vendor' | 'admin' | 'warehouse';

export enum IDeliveryChallanStatus {
  PENDING_CATALOGUING_ASSIGNMENT = 'PENDING_CATALOGUING_ASSIGNMENT',
  EDITING = 'EDITING',
  DISCARDED = 'DISCARDED',
  CLOSED = 'CLOSED',
  READY_FOR_GRN = 'READY_FOR_GRN',
  SENT_FOR_CORRECTION = 'SENT_FOR_CORRECTION',
  GRN_PROCESSED = 'GRN_PROCESSED',
}

export enum DCOnholdReason {
  DC_ASSET_MISSING = 'DC_ASSET_MISSING',
}

export enum CorrectionReasonType {
  WRONG_ITEM = 'WRONG_ITEM',
  WRONG_SIZE = 'WRONG_SIZE',
  WRONG_COLOR = 'WRONG_COLOR',
  WRONG_MRP = 'WRONG_MRP',
  OTHER = 'OTHER',
}

export enum CorrectionStatus {
  OK = 'OK',
  SENT_FOR_CORRECTION = 'SENT_FOR_CORRECTION',
  MARKED_MISSING = 'MARKED_MISSING',
}

export interface IExcessItemsEntry {
  excessQuantity?: number;
  notes?: string;
  assets?: IAsset[];
  createdBy?: string;
  createdOn?: string;
}

export interface ICorrectionPreviewLine {
  skuCode: string;
  expectedQuantity: number;
  grnedQuantity: number;
  shortQuantity: number;
}

export interface ICorrectionSubmissionLine {
  skuCode: string;
  correctionQuantity: number;
  missingQuantity: number;
  reason: CorrectionReasonType;
  reasonDescription: string;
}

export interface ICorrectionSubmission {
  corrections: ICorrectionSubmissionLine[];
  excessItems?: IExcessItemsEntry;
}

export enum DCItemQCStatus {
  PASSED = 'PASSED',
  REJECTED = 'REJECTED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
}

export enum PurchaseType {
  PO = 'PO',
  DC = 'DC',
}

export interface IDeliveryChallan {
  id: number;
  type: PurchaseType;
  referenceNumber: string;
  documentDate: Dayjs;
  paymentTerms: string;
  poTerms: string;
  vendorId: number;
  facilityId: number;
  expectedDeliveryDate: Dayjs;
  arrivedOn?: Dayjs;
  ewbNumber?: string;
  invoiceNumber?: string;
  vendorDCAssetIds: [number];
  closedOn?: Dayjs;
  status: IDeliveryChallanStatus;
  expectedTotalMRP: number;
  expectedTotalCostPrice: number;
  expectedTotalItemCount: number;
  actualTotalMRP: number;
  actualTotalCostPrice: number;
  actualTotalItemCount: number;
  currentLotId: number;
  summary: IDeliveryChallanItemScanSummary;
  lots: { [lotId: number]: IDeliveryChallanLot };
  unicomPOCode?: string;

  currency?: CurrencyCode;

  receivedBoxesCount?: number;
  damagedBoxesCount?: number;
  receivedItemsCount?: number;

  // international purchases
  importedOn?: string;
  currencyConversionRate?: number;
  chargeableWeightInKg?: number;
  totalFreightCost?: number;
  insuranceCost?: number;
  customsDuty?: number;
  igst?: number;
  poAssetIds: { [key: string]: number };
  awb?: string;

  orderLineItemId?: number;

  unicomStatusCode?: string;
  unicomLastSyncedAt?: string;
  unicomPOData?: Record<string, any>;

  lineSheetAssetId?: number;
  dcPdfAssetId?: number;
  importAssetIds?: number[];
  numberOfItems?: number;
  numberOfUniqueStyles?: number;
  cataloguerUserId?: number;
  cataloguingAssignedAt?: string;
  onhold?: boolean;
  onholdReason?: DCOnholdReason;
  correctionSentAt?: string;
  correctionsResetAt?: string;
  totalCorrectionQuantity?: number;
  totalMissingQuantity?: number;
  primaryCorrectionReason?: CorrectionReasonType;
  excessItems?: IExcessItemsEntry;
  correctionAttemptCount?: number;
}

export interface IDeliveryChallanLot {
  id: number;
  unicomPOCode?: string;
  scanSummary: IDeliveryChallanItemScanSummary;
}

export interface IDeliveryChallanItemScanSummary {
  totalItems: number;
  pendingScan: number;
  qcPassed: number;
  qcFailed: number;
  qcRejected: number;
}

export interface IDeliveryChallanItemUploadInput {
  skuCode: string;
  mrp: number;
  costPrice: number;
  quantity: number;
}

export interface IDeliveryChallanItem {
  id: number;
  skuId: number;
  skuCode: string;
  qcStatus: DCItemQCStatus;
  failureReason: string;
  remarks: string;
  mrp: string;
  enabled: boolean;
  skuEntry: ISKU;
  unicomPOCode?: string;
  unicomLastSyncedAt?: string;
  correctionStatus?: CorrectionStatus;
  correctionReason?: CorrectionReasonType;
  correctionReasonDescription?: string;
  correctionSentAt?: string;
}

export interface IDeliveryChallanSku {
  skuCode: string;

  mrp: string;
  costPrice: number;
  landedPriceWithGST: number;
  landedPriceWithoutGST: number;

  total: number;
  totalPending: number;
  totalPassed: number;
  totalRejected: number;
  totalFailed: number;

  skuEntry: ISKU;

  enabled: boolean;
}

export interface IDeliveryChallanComment {
  id: number;
  text: string;
  resolved: boolean;
  assets: IAsset[];
  createdBy?: string;
  createdOn?: string;
}
